import { FeatureFlagService, FeaturesToken, UserService } from '@vallia-doc-flow/ui-auth';

export interface Option {
  name: string;
  iconClass?: string;
  testAttr?: string;
  customClass?: string;
}

export interface Menu {
  sectionId: string;
  sectionCustomClass?: string;
  sectionTestAttr?: string;
  sectionOptions: MenuOption[];
}
export interface MenuOption {
  routerLink: string;
  parentRoutePath: string;
  id: string;
  testAttr?: string;
  customClass?: string;
  isDisabled?: boolean;
  option: Option;
  sectionId: string;
  subMenu?: SubMenuOption[];
}
export interface SubMenuOption {
  routerLink: string;
  parentRoutePath: string;
  id: string;
  testAttr?: string;
  customClass?: string;
  isDisabled?: boolean;
  option: Option;
  sectionId: string;
}
export function shouldCheckDocumentIntelligence(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.LOANREADY_DOCUMENT_INTELLIGENCE);
}

export function shouldCheckUnderwriterCondition(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.UNDERWRITER_CONDITION);
}

export function shouldCheckTestModel(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.DOC_ANALYZER);
}

export function shouldCheckValliaAsk(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.VALLIA_ASK);
}

export function shouldCheckAuditAssist(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.AUDIT);
}
export function shouldCheckOFAC(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.OFAC);
}
export function shouldCheckConfigSettings(featureService: FeatureFlagService, userService: UserService): boolean {
  return featureService.enabledFeature(FeaturesToken.CONFIG_SETTINGS) && userService.enableFeature() === true;
}
export function shouldCheckDocumentAnalyzer(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.DOCUMENT_ANALYZER);
}
export function shouldCheckReliant(featureService: FeatureFlagService): boolean {
  return featureService.enabledFeature(FeaturesToken.RELIANT);
}
export function shouldCheckUnderwriterUrl(): boolean {
  return window.location.href.includes('https://brimma-vallia-docflow-v2-dev-as.azurewebsites.net') ? true : false;
}

export const adminMenu: Menu[] = [
  {
    sectionId: 'vallia-doc-flow-sec-1',
    sectionOptions: [
      {
        routerLink: '/admin/tenants',
        parentRoutePath: '/admin/tenants',
        id: 'tenants-screen',
        testAttr: 'vallia-docFlow-tenants-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Tenants',
          iconClass: 'tenants-icon',
        },
      },
      {
        routerLink: '',
        parentRoutePath: '',
        isDisabled: true,
        id: 'doc-classification-screen',
        testAttr: 'vallia-docFlow-doc-classify-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Doc Classification',
          iconClass: 'document-icon',
        },
      },
      {
        routerLink: '',
        parentRoutePath: '',
        isDisabled: true,
        id: 'flow-rules-screen',
        testAttr: 'vallia-docFlow-flow-rules-lib-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Flow Rules Library',
          iconClass: 'flow-rules-icon',
        },
      },
      {
        routerLink: '/admin/flows-library',
        parentRoutePath: '/admin/flows-library',
        isDisabled: false,
        id: 'flows-screen',
        testAttr: 'vallia-docFlow-flows-lib-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Flows Library',
          iconClass: 'flow-icon',
        },
      },
      {
        routerLink: '',
        parentRoutePath: '',
        isDisabled: true,
        id: 'actions-screen',
        testAttr: 'vallia-docFlow-actions-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Actions',
          iconClass: 'actions-icon',
        },
      },
    ],
    sectionTestAttr: 'vallia-docFlow-admin-menu-sec-1',
  },
  {
    sectionId: 'vallia-doc-flow-sec-1-2',
    sectionOptions: [
      {
        routerLink: '',
        parentRoutePath: '',
        id: 'settings-screen',
        testAttr: 'vallia-docFlow-settings-screen',
        sectionId: 'section-2',
        isDisabled: true,
        option: {
          name: 'Settings',
          iconClass: 'settings-icon',
        },
      },
    ],
    sectionTestAttr: 'vallia-docFlow-admin-menu-sec-2',
  },
];

export const userMenu = (featureService: FeatureFlagService, userService: UserService): Menu[] => {
  const isUnderwriterCondition = shouldCheckUnderwriterCondition(featureService);
  // const showUnderwriterSubmenu = isUnderwriterCondition && shouldCheckUnderwriterUrl();
  const showValliaAskMenu = shouldCheckValliaAsk(featureService);
  const showCheckConfigSettings = shouldCheckConfigSettings(featureService, userService);
  const showDocumentAnalyzer = shouldCheckDocumentAnalyzer(featureService);
  const showReliant = shouldCheckReliant(featureService);
  const showDocumentIntelligence = shouldCheckDocumentIntelligence(featureService);

  const menuItem = isUnderwriterCondition
    ? {
        routerLink: '',
        parentRoutePath: '/user/flows',
        isDisabled: false,
        id: 'flows-screen',
        testAttr: 'vallia-docFlow-flows-screen',
        sectionId: 'vallia-doc-flow-sec-1',
        option: {
          name: 'Flows',
          iconClass: 'flow-icon',
        },
        subMenu: [
          {
            routerLink: '/user/loans/userLoans',
            parentRoutePath: '/user/loans/',
            isDisabled: false,
            id: 'loans-screen',
            testAttr: 'vallia-docFlow-loans-screen',
            option: {
              name: 'Compliance & QC',
              iconClass: '',
            },
            sectionId: 'vallia-doc-flow-sec-1',
          },

          {
            routerLink: 'user/underwriter/underwritingLoans',
            parentRoutePath: '/user/underwriter/',
            id: 'underwriter-screen',
            isDisabled: false,
            testAttr: 'vallia-docFlow-underwriter-screen',
            option: {
              name: 'Underwriting Condition',
              iconClass: '',
            },
            sectionId: 'vallia-doc-flow-sec-1',
          },
        ],
      }
    : {
        routerLink: '/user/loans/userLoans',
        parentRoutePath: '/user/loans/',
        isDisabled: false,
        id: 'loans-screen',
        testAttr: 'vallia-docFlow-loans-screen',
        option: {
          name: 'Loans',
          iconClass: 'document-icon',
        },
        sectionId: 'vallia-doc-flow-sec-1',
      };
  const menuItems = [
    {
      sectionId: 'vallia-doc-flow-sec-1',
      sectionOptions: [
        {
          routerLink: '/user/dashboard',
          parentRoutePath: '/user/dashboard',
          id: 'dashboard-screen',
          testAttr: 'vallia-docFlow-dashboard-screen',
          sectionId: 'vallia-doc-flow-sec-1',
          option: {
            name: 'Dashboard',
            iconClass: 'dashboard-icon',
          },
        },
        menuItem,
        ...(showDocumentIntelligence
          ? [
              {
                routerLink: 'user/income/incomeAndAssets',
                parentRoutePath: 'user/income/',
                id: 'income-screen',
                isDisabled: false,
                testAttr: 'vallia-docFlow-income-screen',
                option: {
                  name: 'LoanReady Document Intelligence',
                  iconClass: 'document-icon',
                },
                sectionId: 'vallia-doc-flow-sec-1',
              },
            ]
          : []),
        ...(showValliaAskMenu
          ? [
              {
                routerLink: '',
                parentRoutePath: '/user/assist',
                isDisabled: false,
                id: 'assist-screen',
                testAttr: 'vallia-docFlow-assist-screen',
                sectionId: 'vallia-doc-flow-sec-1',
                option: {
                  name: 'Vallia Assistants',
                  iconClass: 'flow-rules-icon',
                },
                subMenu: [
                  // {
                  //   routerLink: 'user/income/incomeAndAssets',
                  //   parentRoutePath: 'user/income/',
                  //   id: 'income-screen',
                  //   isDisabled: false,
                  //   testAttr: 'vallia-docFlow-income-screen',
                  //   option: {
                  //     name: 'LoanReady Document Intelligence',
                  //     iconClass: '',
                  //   },
                  //   sectionId: 'vallia-doc-flow-sec-1',
                  // },
                  // {
                  //   routerLink: 'user/income/incomeLoans',
                  //   parentRoutePath: '/user/income/',
                  //   id: 'income-screen',
                  //   isDisabled: false,
                  //   testAttr: 'vallia-docFlow-income-screen',
                  //   option: {
                  //     name: 'Calculate Income',
                  //     iconClass: '',
                  //   },
                  //   sectionId: 'vallia-doc-flow-sec-1',
                  // },
                  // {
                  //   routerLink: 'user/income/askLoans',
                  //   parentRoutePath: '/user/income/',
                  //   id: 'income-screen',
                  //   isDisabled: false,
                  //   testAttr: 'vallia-docFlow-income-screen',
                  //   option: {
                  //     name: 'Ask & Analyze',
                  //     iconClass: '',
                  //   },
                  //   sectionId: 'vallia-doc-flow-sec-1',
                  // },
                  ...(showDocumentAnalyzer
                    ? [
                        // {
                        //   routerLink: 'user/income/doc-analyzer',
                        //   parentRoutePath: '/user/income/',
                        //   id: 'income-screen',
                        //   isDisabled: false,
                        //   testAttr: 'vallia-docFlow-income-screen',
                        //   option: {
                        //     name: 'Document Analyzer',
                        //     iconClass: '',
                        //   },
                        //   sectionId: 'vallia-doc-flow-sec-1',
                        // },
                        {
                          routerLink: 'user/income/rule-spec-analyzer',
                          parentRoutePath: '/user/income/',
                          id: 'income-screen',
                          isDisabled: false,
                          testAttr: 'vallia-docFlow-income-screen',
                          option: {
                            name: 'Rules Builder',
                            iconClass: '',
                          },
                          sectionId: 'vallia-doc-flow-sec-1',
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
        {
          routerLink: '/user/file-upload',
          parentRoutePath: '/user/file-upload',
          id: 'upload-screen',
          testAttr: 'vallia-docFlow-upload-screen',
          sectionId: 'vallia-doc-flow-upload-screen-3',
          isDisabled: false,
          option: {
            name: 'File Upload',
            iconClass: 'file-upload-icon',
          },
        },
        {
          routerLink: '',
          parentRoutePath: '',
          isDisabled: true,
          id: 'flow-rules-screen',
          testAttr: 'vallia-docFlow-flow-rules-screen',
          sectionId: 'vallia-doc-flow-sec-1',
          option: {
            name: 'Flow Rules',
            iconClass: 'flow-rules-icon',
          },
        },
        {
          routerLink: '',
          parentRoutePath: '',
          isDisabled: true,
          id: 'actions-screen',
          testAttr: 'vallia-docFlow-actions-screen',
          sectionId: 'vallia-doc-flow-sec-1',
          option: {
            name: 'Actions',
            iconClass: 'actions-icon',
          },
        },
      ],
      sectionTestAttr: 'vallia-docFlow-user-menu-sec-1',
    },
    {
      sectionId: 'vallia-doc-flow-sec-1-2',
      sectionOptions: [
        {
          routerLink: '',
          parentRoutePath: '',
          id: 'settings-screen',
          testAttr: 'vallia-docFlow-settings-screen',
          sectionId: 'vallia-doc-flow-sec-2',
          isDisabled: !showReliant && !userService.enableFeature(),
          option: {
            name: 'Settings',
            iconClass: 'settings-icon',
          },
          subMenu: [
            ...(showReliant
              ? [
                  {
                    routerLink: 'playground',
                    parentRoutePath: 'playground',
                    id: 'upload-screen',
                    testAttr: 'vallia-docFlow-file-upload-screen',
                    sectionId: 'vallia-doc-flow-file-upload-screen-3',
                    isDisabled: false,
                    option: {
                      name: 'Playground',
                      iconClass: 'document-icon',
                    },
                  },
                ]
              : []),
            ...(showCheckConfigSettings
              ? [
                  {
                    routerLink: '/user/configuration-settings',
                    parentRoutePath: '/user/configuration-settings',
                    isDisabled: false,
                    id: 'config-settings',
                    testAttr: 'vallia-docFlow-config-settings',
                    option: {
                      name: 'Configuration Settings',
                      iconClass: '',
                    },
                    sectionId: 'vallia-doc-flow-sec-1',
                  },
                ]
              : []),
          ],
        },
      ],
      sectionTestAttr: 'vallia-docFlow-admin-menu-sec-2',
    },
  ];

  return menuItems;
};

export function getTestModelMenu(featureService: FeatureFlagService): Menu[] {
  const showTestModel = shouldCheckTestModel(featureService);
  const showAuditAssist = shouldCheckAuditAssist(featureService);
  const showOfacAnalysis = shouldCheckOFAC(featureService);

  const menu: Menu[] = [];

  if (showTestModel || showAuditAssist || showOfacAnalysis) {
    menu.push(settingsSection);
  }

  if (showAuditAssist || showOfacAnalysis) {
    const filteredSubMenu = [];

    if (showAuditAssist) {
      filteredSubMenu.push({
        routerLink: 'playground/rule-assist',
        parentRoutePath: 'playground/',
        id: 'audit-assistant',
        isDisabled: false,
        testAttr: 'vallia-docFlow-income-screen',
        option: {
          name: 'Audit Assistant',
          iconClass: '',
        },
        sectionId: 'vallia-doc-flow-sec-1',
      });
    }

    if (showOfacAnalysis) {
      filteredSubMenu.push({
        routerLink: 'playground/rule-assist',
        parentRoutePath: 'playground/',
        id: 'ofac-analysis',
        isDisabled: false,
        testAttr: 'vallia-docFlow-income-screen',
        option: {
          name: 'OFAC Analysis',
          iconClass: '',
        },
        sectionId: 'vallia-doc-flow-sec-1',
      });
    }

    if (filteredSubMenu.length > 0) {
      menu.push({
        sectionId: 'vallia-doc-flow-sec-1',
        sectionOptions: [
          {
            routerLink: '',
            parentRoutePath: '/user/assist',
            isDisabled: false,
            id: 'assist-screen',
            testAttr: 'vallia-docFlow-assist-screen',
            sectionId: 'vallia-doc-flow-sec-1',
            option: {
              name: 'Vallia Assistants',
              iconClass: 'flow-rules-icon',
            },
            subMenu: filteredSubMenu,
          },
        ],
        sectionTestAttr: 'vallia-docFlow-user-menu-sec-1',
      });
    }
  }

  return menu;
}

const settingsSection: Menu = {
  sectionId: 'vallia-doc-flow-sec-1-2',
  sectionOptions: [
    {
      routerLink: 'playground',
      parentRoutePath: 'playground',
      id: 'upload-screen',
      testAttr: 'vallia-docFlow-file-upload-screen',
      sectionId: 'vallia-doc-flow-file-upload-screen-3',
      isDisabled: false,
      option: {
        name: 'Playground',
        iconClass: 'document-icon',
      },
    },
  ],
  sectionTestAttr: 'vallia-docFlow-admin-menu-sec-2',
};
